import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, MODELS, SECTION_KEYS, DISCIPLINES, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Save, Sparkles, Plus, Trash2, User2, BookMarked, FileSpreadsheet, FileText, FileType2, Loader2, ShieldCheck, History, PanelRightOpen, Compass, Printer, Award } from "lucide-react";
import { motion } from "framer-motion";
import RichEditor from "@/components/RichEditor";
import CompliancePanel from "@/components/CompliancePanel";
import VersionsPanel from "@/components/VersionsPanel";
// Voice-to-draft removed per product decision.
import AIFullProject from "@/components/AIFullProject";

const RIGHT_TABS = [
  { key: "ai", label: "AI Writer", icon: Sparkles },
  { key: "coach", label: "SWOT", icon: Award },
  { key: "team", label: "Team & CV", icon: User2 },
  { key: "budget", label: "Budget", icon: FileSpreadsheet },
  { key: "refs", label: "References", icon: BookMarked },
  { key: "compliance", label: "Compliance", icon: ShieldCheck },
  { key: "history", label: "History", icon: History },
];

export default function ProposalBuilder() {
  const { id } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [activeKey, setActiveKey] = useState("abstract");
  const [saving, setSaving] = useState(false);
  const [rightTab, setRightTab] = useState("ai");
  const [aiInstruction, setAiInstruction] = useState("");
  const [aiModel, setAiModel] = useState(0);
  const [aiStreaming, setAiStreaming] = useState(false);
  const [aiPreview, setAiPreview] = useState("");
  const [agencyPreset, setAgencyPreset] = useState("Generic / Other");

  useEffect(() => {
    api.get(`/proposals/${id}`).then(r => setP(r.data)).catch(() => { toast.error("Not found"); nav("/app"); });
  }, [id, nav]);

  const setSection = (key, val) => setP((prev)=>({ ...prev, sections: { ...prev.sections, [key]: val }}));

  const applyFullProject = ({ suggested_title, sections, references }) => {
    setP(prev => {
      const merged = { ...(prev.sections || {}) };
      Object.entries(sections || {}).forEach(([k, html]) => { if (html) merged[k] = html; });
      const nextRefs = (references || []).map(r => {
        const authors = (r.authors || []).join(", ");
        const parts = [
          authors,
          r.year ? `(${r.year})` : "",
          r.title || "",
          r.venue || "",
          r.doi ? `DOI: ${r.doi}` : (r.url || ""),
        ].filter(Boolean);
        return {
          citation: parts.join(". "),
          title: r.title || "",
          authors,
          year: r.year || "",
          doi: r.doi || "",
          url: r.url || "",
          venue: r.venue || "",
        };
      });
      return {
        ...prev,
        title: (!prev.title || prev.title === "Untitled proposal") ? (suggested_title || prev.title) : prev.title,
        sections: merged,
        references: nextRefs.length ? nextRefs : (prev.references || []),
      };
    });
    // The autosave useEffect will persist once `p.sections` changes — no manual save needed.
  };

  const save = async (silent=false) => {
    if (!p) return;
    setSaving(true);
    try {
      const { data } = await api.put(`/proposals/${id}`, {
        title: p.title, agency: p.agency, discipline: p.discipline,
        sections: p.sections, budget_items: p.budget_items, team: p.team, references: p.references, status: p.status,
      });
      setP(data);
      if (!silent) toast.success("Saved");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  useEffect(() => {
    if (!p) return;
    const t = setTimeout(() => save(true), 1500);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [p?.title, p?.agency, p?.discipline, p?.sections, p?.budget_items, p?.team, p?.references, p?.status]);

  const runAI = async (sectionKey) => {
    const m = MODELS[aiModel];
    setAiStreaming(true); setAiPreview("");
    const token = localStorage.getItem("gf_token");
    try {
      const res = await fetch(`${API}/ai/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          proposal_id: id, section_key: sectionKey,
          model_provider: m.provider, model_name: m.name,
          instruction: aiInstruction, tone: "formal, academic, persuasive",
        }),
      });
      if (!res.ok) throw new Error("AI request failed");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      let acc = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop() || "";
        for (const part of parts) {
          const line = part.replace(/^data:\s*/, "").trim();
          if (!line) continue;
          try {
            const obj = JSON.parse(line);
            if (obj.delta) { acc += obj.delta; setAiPreview(acc); }
            if (obj.error) toast.error(obj.error);
          } catch { /* ignore */ }
        }
      }
    } catch (e) { toast.error(e.message || "AI failed"); }
    finally { setAiStreaming(false); }
  };

  const acceptAI = () => {
    if (!aiPreview) return;
    const html = markdownToHtml(aiPreview);
    setSection(activeKey, (p.sections?.[activeKey] || "") + html);
    setAiPreview("");
    setAiInstruction("");
    toast.success("Inserted into draft");
  };

  const exportFile = async (kind) => {
    const url = `${API}/proposals/${id}/export/${kind}`;
    const res = await fetch(url, { headers: { Authorization: `Bearer ${localStorage.getItem("gf_token")}` }});
    if (!res.ok) return toast.error("Export failed");
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${p.title || "proposal"}.${kind === "xlsx" ? "xlsx" : kind}`;
    a.click();
    toast.success(`Downloaded ${kind.toUpperCase()}`);
  };

  if (!p) return <div className="p-10 text-muted-foreground">Loading proposal…</div>;
  const activeMeta = SECTION_KEYS.find(s => s.key === activeKey);

  return (
    <div className="min-h-screen paper">
      {/* Top bar */}
      <div className="sticky top-0 z-20 border-b border-border bg-background/85 backdrop-blur-md">
        <div className="max-w-[1500px] mx-auto px-6 py-3 flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={()=>nav("/app")} data-testid="back-dashboard"><ArrowLeft className="w-4 h-4 mr-1"/>Dashboard</Button>
          <Input
            value={p.title}
            onChange={(e)=>setP({...p, title: e.target.value})}
            className="flex-1 font-display text-xl border-none shadow-none focus-visible:ring-0 bg-transparent"
            data-testid="proposal-title"
          />
          <div className="flex items-center gap-2">
            {saving ? <span className="text-xs text-muted-foreground"><Loader2 className="inline w-3 h-3 mr-1 animate-spin"/>Saving…</span> : <span className="text-xs text-muted-foreground">All changes saved</span>}
            <AIFullProject
              module="proposal"
              title={p.title}
              defaultIdea={p.abstract_seed || ""}
              testidPrefix="prop-fp"
              docId={id}
              saveDoc={()=>save(true)}
              onApply={applyFullProject}
            />
            <Button variant="outline" size="sm" onClick={()=>save(false)} data-testid="save-btn"><Save className="w-4 h-4 mr-1"/>Save</Button>
            <Button size="sm" onClick={()=>exportFile("pdf")} data-testid="export-pdf" className="rounded-full"><FileType2 className="w-4 h-4 mr-1"/>PDF</Button>
            <Button size="sm" variant="outline" onClick={()=>exportFile("docx")} data-testid="export-docx"><FileText className="w-4 h-4 mr-1"/>DOCX</Button>
            <Button size="sm" variant="outline" onClick={()=>exportFile("xlsx")} data-testid="export-xlsx"><FileSpreadsheet className="w-4 h-4 mr-1"/>XLSX</Button>
          </div>
        </div>
      </div>

      <div className="max-w-[1500px] mx-auto px-6 py-6 grid grid-cols-12 gap-6">
        {/* Left: outline */}
        <aside className="col-span-12 lg:col-span-2">
          <div className="card-crisp rounded-lg p-3 sticky top-24">
            <div className="overline mb-3 px-1">Outline</div>
            <ul className="space-y-1">
              {SECTION_KEYS.map((s) => {
                const filled = ((p.sections?.[s.key] || "").replace(/<[^>]+>/g," ").trim()).length > 0;
                return (
                  <li key={s.key}>
                    <button
                      onClick={()=>setActiveKey(s.key)}
                      data-testid={`outline-${s.key}`}
                      className={`w-full text-left px-2 py-1.5 rounded-md text-[13px] flex items-center justify-between transition-colors ${activeKey===s.key ? "bg-primary/10 text-primary" : "hover:bg-secondary"}`}
                    >
                      <span className="truncate">{s.label}</span>
                      <span className={`w-1.5 h-1.5 rounded-full ${filled ? "bg-primary" : "bg-muted-foreground/30"}`}/>
                    </button>
                  </li>
                );
              })}
            </ul>
            <div className="mt-3 pt-3 border-t border-border">
              <div className="overline mb-2">Meta</div>
              <div className="space-y-2">
                <div>
                  <Label className="text-xs">Discipline</Label>
                  <Select value={p.discipline} onValueChange={(v)=>setP({...p, discipline: v})}>
                    <SelectTrigger data-testid="meta-discipline"><SelectValue/></SelectTrigger>
                    <SelectContent>{DISCIPLINES.map(d=><SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Target agency</Label>
                  <Input value={p.agency} onChange={(e)=>setP({...p, agency: e.target.value})} data-testid="meta-agency"/>
                </div>
              </div>
            </div>
          </div>
        </aside>

        {/* Center: editor */}
        <section className="col-span-12 lg:col-span-7">
          <motion.div key={activeKey} initial={{opacity:0, y:6}} animate={{opacity:1, y:0}} transition={{duration:0.2}}>
            <div className="flex items-end justify-between mb-3">
              <div>
                <div className="overline">Section</div>
                <h2 className="font-display text-3xl">{activeMeta?.label}</h2>
              </div>
              <div className="text-xs text-muted-foreground">
                {(p.sections?.[activeKey] || "").replace(/<[^>]+>/g," ").trim().split(/\s+/).filter(Boolean).length} words
              </div>
            </div>
            <RichEditor
              value={p.sections?.[activeKey] || ""}
              onChange={(html)=>setSection(activeKey, html)}
              placeholder="Type here — or open the AI Writer on the right for a first draft."
            />
          </motion.div>
        </section>

        {/* Right: tabs */}
        <aside className="col-span-12 lg:col-span-3">
          <div className="sticky top-24 space-y-3">
            <div className="card-crisp rounded-lg p-1 flex flex-wrap gap-0.5">
              {RIGHT_TABS.map((t) => {
                const active = rightTab === t.key;
                const Icon = t.icon;
                return (
                  <button
                    key={t.key}
                    onClick={()=>setRightTab(t.key)}
                    data-testid={`right-tab-${t.key}`}
                    className={`flex-1 flex items-center justify-center gap-1 text-[11px] px-2 py-1.5 rounded-md transition-colors ${active ? "bg-primary/12 text-primary" : "hover:bg-secondary"}`}
                    title={t.label}
                  >
                    <Icon className="w-3.5 h-3.5"/>
                    <span className="hidden xl:inline">{t.label}</span>
                  </button>
                );
              })}
            </div>

            {rightTab === "ai" && (
              <AIPanel
                aiModel={aiModel} setAiModel={setAiModel}
                instruction={aiInstruction} setInstruction={setAiInstruction}
                onRun={()=>runAI(activeKey)} streaming={aiStreaming}
                preview={aiPreview} onAccept={acceptAI} onDiscard={()=>setAiPreview("")}
                onVoiceInsert={(html)=>setSection(activeKey, (p.sections?.[activeKey]||"") + html)}
              />
            )}
            {rightTab === "coach" && <CoachPanel proposalId={id}/>}
            {rightTab === "team" && <TeamPanel p={p} setP={setP}/>}
            {rightTab === "budget" && <BudgetPanel p={p} setP={setP}/>}
            {rightTab === "refs" && <RefsPanel p={p} setP={setP}/>}
            {rightTab === "compliance" && <CompliancePanel proposal={p} agencyKey={agencyPreset} onAgencyChange={setAgencyPreset}/>}
            {rightTab === "history" && (
              <VersionsPanel
                proposalId={id}
                sectionKey={activeKey}
                currentContent={p.sections?.[activeKey] || ""}
                onRestore={(c)=>setSection(activeKey, c)}
              />
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}

function markdownToHtml(md) {
  if (!md) return "";
  const esc = (s) => s.replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;");
  const lines = md.split(/\r?\n/);
  const out = [];
  let inList = null;
  const flush = () => { if (inList) { out.push(`</${inList}>`); inList = null; } };
  for (const raw of lines) {
    const line = raw;
    if (/^\s*#{3}\s+/.test(line)) { flush(); out.push(`<h3>${esc(line.replace(/^\s*###\s+/, ""))}</h3>`); continue; }
    if (/^\s*#{2}\s+/.test(line)) { flush(); out.push(`<h2>${esc(line.replace(/^\s*##\s+/, ""))}</h2>`); continue; }
    if (/^\s*#\s+/.test(line))    { flush(); out.push(`<h1>${esc(line.replace(/^\s*#\s+/, ""))}</h1>`); continue; }
    if (/^\s*[-*]\s+/.test(line)) {
      if (inList !== "ul") { flush(); out.push("<ul>"); inList = "ul"; }
      out.push(`<li>${inline(esc(line.replace(/^\s*[-*]\s+/, "")))}</li>`); continue;
    }
    if (/^\s*\d+\.\s+/.test(line)) {
      if (inList !== "ol") { flush(); out.push("<ol>"); inList = "ol"; }
      out.push(`<li>${inline(esc(line.replace(/^\s*\d+\.\s+/, "")))}</li>`); continue;
    }
    if (line.trim() === "") { flush(); continue; }
    flush();
    out.push(`<p>${inline(esc(line))}</p>`);
  }
  flush();
  return out.join("\n");
}
function inline(s) {
  return s
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>")
    .replace(/`([^`]+)`/g, "<code>$1</code>");
}

function CoachPanel({ proposalId }) {
  const [r, setR] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const { api } = await import("@/lib/api");
      const { data } = await api.post(`/proposals/${proposalId}/swot`, { proposal_id: proposalId, model_provider:"anthropic", model_name:"claude-sonnet-4-6" });
      setR(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Coach failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="card-crisp rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="font-display text-xl flex items-center gap-2"><Award className="w-5 h-5 text-primary"/>SWOT &amp; Coach</div>
        <Button size="sm" variant="outline" onClick={run} disabled={busy} className="rounded-full" data-testid="swot-run">{busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <Sparkles className="w-4 h-4"/>}</Button>
      </div>
      {!r && <div className="text-xs text-muted-foreground">Analyse your profile + this proposal for SWOT and a next-steps plan.</div>}
      {r && (
        <div className="space-y-3 text-sm max-h-[520px] overflow-auto pr-1" data-testid="swot-result">
          <div className="grid grid-cols-2 gap-2 text-center">
            <div className="rounded-md border border-primary p-2 bg-primary/5"><div className="overline">Fit</div><div className="font-display text-2xl text-primary" style={{fontFeatureSettings:'"lnum" 1'}}>{r.fit_score}%</div></div>
            <div className="rounded-md border border-border p-2"><div className="overline">Compete</div><div className="font-display text-2xl" style={{fontFeatureSettings:'"lnum" 1'}}>{r.competitiveness_score}%</div></div>
          </div>
          {r.coach_message && <div className="italic text-muted-foreground border-l-2 border-primary/40 pl-3">{r.coach_message}</div>}
          <Group title="Strengths" items={r.strengths} color="text-emerald-700"/>
          <Group title="Weaknesses" items={r.weaknesses} color="text-red-700"/>
          <Group title="Opportunities" items={r.opportunities} color="text-sky-700"/>
          <Group title="Threats" items={r.threats} color="text-amber-700"/>
          {r.recommendations?.length > 0 && (
            <div><div className="overline mb-1">Recommendations</div><ul className="space-y-1.5">
              {r.recommendations.map((x,i)=>(<li key={i} className="text-xs"><b>{x.area}:</b> {x.suggestion}</li>))}
            </ul></div>
          )}
          {r.next_actions?.length > 0 && <Group title="Next actions" items={r.next_actions} color="text-primary"/>}
          {r.best_agency_matches?.length > 0 && (
            <div><div className="overline mb-1">Best-fit agencies</div><div className="flex flex-wrap gap-1">{r.best_agency_matches.map((a,i)=>(<span key={i} className="px-2 py-1 text-[11px] rounded-full bg-primary/10 text-primary">{a}</span>))}</div></div>
          )}
        </div>
      )}
    </div>
  );
}
function Group({ title, items, color }) {
  if (!items?.length) return null;
  return (<div><div className={`overline mb-1 ${color}`}>{title}</div><ul className="space-y-1 text-xs">{items.map((s,i)=><li key={i}>• {s}</li>)}</ul></div>);
}

function AIPanel({ aiModel, setAiModel, instruction, setInstruction, onRun, streaming, preview, onAccept, onDiscard, onVoiceInsert }) {
  return (
    <div className="card-crisp rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="font-display text-xl flex items-center gap-2"><Sparkles className="w-5 h-5 text-primary"/>AI co-writer</div>
      </div>
      <div className="space-y-3">
        <div>
          <Label className="text-xs">Model</Label>
          <Select value={String(aiModel)} onValueChange={(v)=>setAiModel(Number(v))}>
            <SelectTrigger data-testid="ai-model-select"><SelectValue/></SelectTrigger>
            <SelectContent>{MODELS.map((m,i)=><SelectItem key={i} value={String(i)}>{m.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Instruction (optional)</Label>
          <Textarea value={instruction} onChange={(e)=>setInstruction(e.target.value)} placeholder="e.g., emphasise clinical impact and cost-effectiveness" className="min-h-[70px]" data-testid="ai-instruction"/>
        </div>
        <div className="flex gap-2">
          <Button className="flex-1 rounded-full" onClick={onRun} disabled={streaming} data-testid="ai-run-btn">
            {streaming ? <><Loader2 className="w-4 h-4 mr-2 animate-spin"/>Drafting…</> : <><Sparkles className="w-4 h-4 mr-2"/>Draft</>}
          </Button>
          {/* Voice-to-draft removed */}
        </div>
        {preview && (
          <div className="border border-primary/40 rounded-md p-3 bg-primary/5 max-h-[320px] overflow-auto whitespace-pre-wrap text-sm" data-testid="ai-preview">
            {preview}
          </div>
        )}
        {preview && !streaming && (
          <div className="flex gap-2">
            <Button className="flex-1 rounded-full" onClick={onAccept} data-testid="ai-accept-btn">Insert into draft</Button>
            <Button variant="outline" className="rounded-full" onClick={onDiscard}>Discard</Button>
          </div>
        )}
      </div>
    </div>
  );
}

function TeamPanel({ p, setP }) {
  const add = () => setP({ ...p, team: [...(p.team||[]), { id: crypto.randomUUID(), name: "", role: "Co-PI", email: "", affiliation: "", discipline: "", orcid: "", bio: "", publications:[], past_projects:[], past_funding:[] }] });
  const upd = (i, k, v) => { const t=[...p.team]; t[i]={...t[i], [k]:v}; setP({...p, team:t}); };
  const del = (i) => setP({ ...p, team: p.team.filter((_, j)=>j!==i) });
  const addSub = (i, key) => { const t=[...p.team]; const list=[...(t[i][key]||[]), { id: crypto.randomUUID() }]; t[i]={...t[i], [key]:list}; setP({...p, team:t}); };
  const updSub = (i, key, j, field, v) => { const t=[...p.team]; const list=[...t[i][key]]; list[j]={...list[j], [field]:v}; t[i]={...t[i], [key]:list}; setP({...p, team:t}); };
  const delSub = (i, key, j) => { const t=[...p.team]; t[i]={...t[i], [key]: t[i][key].filter((_,x)=>x!==j)}; setP({...p, team:t}); };

  return (
    <div className="card-crisp rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="font-display text-xl flex items-center gap-2"><User2 className="w-5 h-5 text-primary"/>Team & CV</div>
        <Button size="sm" variant="ghost" onClick={add} data-testid="team-add"><Plus className="w-4 h-4"/></Button>
      </div>
      <div className="space-y-3 max-h-[520px] overflow-auto pr-1">
        {(p.team||[]).map((m, i)=>(
          <div key={m.id} className="border border-border rounded-md p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs text-muted-foreground">Member #{i+1}</div>
              <button onClick={()=>del(i)} className="text-muted-foreground hover:text-destructive" data-testid={`team-del-${i}`}><Trash2 className="w-3.5 h-3.5"/></button>
            </div>
            <Input className="mb-2" placeholder="Full name" value={m.name} onChange={(e)=>upd(i,"name",e.target.value)} data-testid={`team-name-${i}`}/>
            <div className="grid grid-cols-2 gap-2 mb-2">
              <Select value={m.role} onValueChange={(v)=>upd(i,"role",v)}>
                <SelectTrigger><SelectValue/></SelectTrigger>
                <SelectContent>{["PI","Co-PI","Convener","Organizing Secretary","Collaborator"].map(r=><SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
              <Input placeholder="Affiliation" value={m.affiliation} onChange={(e)=>upd(i,"affiliation",e.target.value)}/>
            </div>
            <Input className="mb-2" placeholder="Email" value={m.email} onChange={(e)=>upd(i,"email",e.target.value)}/>
            <Input className="mb-2" placeholder="ORCID (optional)" value={m.orcid} onChange={(e)=>upd(i,"orcid",e.target.value)}/>
            <Textarea className="mb-2 min-h-[60px]" placeholder="Short bio" value={m.bio} onChange={(e)=>upd(i,"bio",e.target.value)}/>

            <MiniList title="Publications" items={m.publications} onAdd={()=>addSub(i,"publications")} onDel={(j)=>delSub(i,"publications",j)}
              fields={[{k:"title",p:"Title"},{k:"venue",p:"Venue"},{k:"year",p:"Year"}]}
              onChange={(j,k,v)=>updSub(i,"publications",j,k,v)}/>

            <MiniList title="Past Projects" items={m.past_projects} onAdd={()=>addSub(i,"past_projects")} onDel={(j)=>delSub(i,"past_projects",j)}
              fields={[{k:"title",p:"Title"},{k:"role",p:"Role"},{k:"duration",p:"Duration"}]}
              onChange={(j,k,v)=>updSub(i,"past_projects",j,k,v)}/>

            <MiniList title="Previous Funding" items={m.past_funding} onAdd={()=>addSub(i,"past_funding")} onDel={(j)=>delSub(i,"past_funding",j)}
              fields={[{k:"agency",p:"Agency"},{k:"grant_title",p:"Grant title"},{k:"amount",p:"Amount"},{k:"year",p:"Year"}]}
              onChange={(j,k,v)=>updSub(i,"past_funding",j,k,v)}/>
          </div>
        ))}
      </div>
    </div>
  );
}
function MiniList({ title, items = [], onAdd, onDel, onChange, fields }) {
  return (
    <div className="mt-3">
      <div className="flex items-center justify-between mb-1">
        <div className="overline">{title}</div>
        <button onClick={onAdd} className="text-primary text-xs hover:underline">+ Add</button>
      </div>
      <div className="space-y-2">
        {items.map((it, j) => (
          <div key={it.id || j} className="rounded border border-border p-2 space-y-1.5">
            {fields.map(f => (
              <Input key={f.k} className="text-xs h-8" placeholder={f.p} value={it[f.k] || ""} onChange={(e)=>onChange(j, f.k, e.target.value)} />
            ))}
            <div className="text-right">
              <button onClick={()=>onDel(j)} className="text-xs text-muted-foreground hover:text-destructive">Remove</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function BudgetPanel({ p, setP }) {
  const add = () => setP({ ...p, budget_items: [...(p.budget_items||[]), { id: crypto.randomUUID(), category: "Personnel", item: "", amount: "", notes: "" }] });
  const upd = (i, k, v) => { const b=[...p.budget_items]; b[i]={...b[i], [k]:v}; setP({...p, budget_items:b}); };
  const del = (i) => setP({ ...p, budget_items: p.budget_items.filter((_, j)=>j!==i) });
  return (
    <div className="card-crisp rounded-lg p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="font-display text-xl flex items-center gap-2"><FileSpreadsheet className="w-5 h-5 text-primary"/>Budget</div>
        <Button size="sm" variant="ghost" onClick={add} data-testid="budget-add"><Plus className="w-4 h-4"/></Button>
      </div>
      <div className="space-y-2 max-h-[420px] overflow-auto">
        {(p.budget_items||[]).map((b, i)=>(
          <div key={b.id} className="grid grid-cols-12 gap-1 items-center">
            <Select value={b.category} onValueChange={(v)=>upd(i,"category",v)}>
              <SelectTrigger className="col-span-4 h-8 text-xs"><SelectValue/></SelectTrigger>
              <SelectContent>{["Personnel","Equipment","Travel","Consumables","Dissemination","Overhead","Other"].map(c=><SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
            <Input className="col-span-4 h-8 text-xs" placeholder="Item" value={b.item} onChange={(e)=>upd(i,"item",e.target.value)}/>
            <Input className="col-span-3 h-8 text-xs" placeholder="Amount" value={b.amount} onChange={(e)=>upd(i,"amount",e.target.value)}/>
            <button className="col-span-1 text-muted-foreground hover:text-destructive" onClick={()=>del(i)}><Trash2 className="w-3.5 h-3.5"/></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function RefsPanel({ p, setP }) {
  const add = () => setP({ ...p, references: [...(p.references||[]), { id: crypto.randomUUID(), citation: "" }] });
  const upd = (i, v) => { const r=[...p.references]; r[i]={...r[i], citation:v}; setP({...p, references:r}); };
  const del = (i) => setP({ ...p, references: p.references.filter((_, j)=>j!==i) });
  return (
    <div className="card-crisp rounded-lg p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="font-display text-xl flex items-center gap-2"><BookMarked className="w-5 h-5 text-primary"/>References</div>
        <Button size="sm" variant="ghost" onClick={add} data-testid="ref-add"><Plus className="w-4 h-4"/></Button>
      </div>
      <div className="space-y-2 max-h-[420px] overflow-auto">
        {(p.references||[]).map((r, i)=>(
          <div key={r.id} className="flex gap-1">
            <Textarea className="text-xs min-h-[50px]" placeholder="Full citation…" value={r.citation} onChange={(e)=>upd(i,e.target.value)}/>
            <button onClick={()=>del(i)} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-3.5 h-3.5"/></button>
          </div>
        ))}
      </div>
    </div>
  );
}
